import { eventsQueryKeys, useMyUserQuery } from "@ac-expo/api";
import { MyUserType } from "@ac-expo/api-types";
import { useSessionRefresh } from "@ac-expo/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { authClient } from "@web-app/src/services/auth-client";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

type UserContextType = {
  user?: MyUserType;
  disconnect: () => Promise<void>;
  refetchSession: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
};

const UserContext = createContext<UserContextType>({} as UserContextType);

type Props = {
  children: ReactNode;
};

function UserProvider({ children }: Props) {
  const queryClient = useQueryClient();

  const {
    data: session,
    isPending: isSessionLoading,
    refetch: refetchSession,
  } = authClient.useSession();

  const isAuthenticated = !!session?.user;
  const sessionUserId = session?.user?.id;

  useSessionRefresh(isAuthenticated, refetchSession);

  const { data: user, isLoading: isUserLoading, refetch: refetchUser } = useMyUserQuery();

  useEffect(() => {
    if (sessionUserId && sessionUserId !== user?.id) {
      refetchUser();
    }
  }, [sessionUserId, user?.id, refetchUser]);

  // `undefined` until the session has resolved once, then the last observed user id (`null` = guest).
  const previousSessionUserIdRef = useRef<string | null | undefined>(undefined);

  // The public event filtering (Group tickets) runs per session: replay it as soon as the
  // session identity changes, in both directions, instead of waiting for a page reload.
  useEffect(() => {
    if (isSessionLoading) return;

    const currentSessionUserId = sessionUserId ?? null;
    const previousSessionUserId = previousSessionUserIdRef.current;
    previousSessionUserIdRef.current = currentSessionUserId;

    if (previousSessionUserId === undefined || previousSessionUserId === currentSessionUserId) {
      return;
    }

    queryClient.invalidateQueries({ queryKey: eventsQueryKeys.all });
  }, [isSessionLoading, sessionUserId, queryClient]);

  const disconnect = useCallback(async () => {
    await authClient.signOut();
    queryClient.removeQueries();
  }, [queryClient]);

  const isLoading = isSessionLoading || isUserLoading;

  const memoizedValue = useMemo(
    () => ({ user, disconnect, refetchSession, isLoading, isAuthenticated }),
    [user, disconnect, refetchSession, isLoading, isAuthenticated],
  );

  return <UserContext.Provider value={memoizedValue}>{children}</UserContext.Provider>;
}

const useUserContext = () => useContext(UserContext);

export { UserProvider, useUserContext };
